import { Fragment, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  IconButton,
  Tooltip,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from '@chakra-ui/icons';
import Chart from 'react-apexcharts';
import { formatRatio, formatYearMonth } from 'utils/rag';
import { downloadCsvFile, todayStr } from 'utils/csv';
import { KPICard } from './OverallKPICards';

export interface CallRow {
  label: string;
  branch_id: number | null;
  email: string;
  total_leads: number;

  calls_today: number;
  daily_call_avg: number | null;
  in_progress: number;
  dispositioned: number;
  call_attempts: number;
  quoted: number;
  declined: number;
  unable_to_connect: number;
  call_to_quote_ratio: number;
  closing_ratio: number;
  ignored_lead_ratio: number;
  avg_calls_per_lead: number;
  children?: CallRow[];
}

interface CallTrend {
  year_month: string;
  daily_call_avg: number;
  call_to_quote_ratio: number;
  closing_ratio: number;
}

type SortKey = Exclude<keyof CallRow, 'label' | 'email' | 'children'>;

const count = (v: number) => v.toLocaleString();
const avg = (v: number | null) => (v == null ? '-' : v.toFixed(1));

const COLUMNS: { key: SortKey; label: string; fmt: (r: CallRow) => string }[] =
  [
    {
      key: 'total_leads',
      label: 'Total Leads',
      fmt: (r) => count(r.total_leads),
    },
    {
      key: 'call_attempts',
      label: 'Calls Made',
      fmt: (r) => count(r.call_attempts),
    },
    {
      key: 'calls_today',
      label: 'Calls Today',
      fmt: (r) => count(r.calls_today),
    },
    {
      key: 'daily_call_avg',
      label: 'Daily Avg',
      fmt: (r) => avg(r.daily_call_avg),
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      fmt: (r) => count(r.in_progress),
    },
    { key: 'quoted', label: 'Quoted', fmt: (r) => count(r.quoted) },
    { key: 'declined', label: 'Declined', fmt: (r) => count(r.declined) },
    {
      key: 'unable_to_connect',
      label: 'Unable to Connect',
      fmt: (r) => count(r.unable_to_connect),
    },
    {
      key: 'call_to_quote_ratio',
      label: 'Call to Quote',
      fmt: (r) => avg(r.call_to_quote_ratio),
    },
    {
      key: 'closing_ratio',
      label: 'Closing',
      fmt: (r) => formatRatio(r.closing_ratio),
    },
    {
      key: 'ignored_lead_ratio',
      label: 'Ignored Lead',
      fmt: (r) => formatRatio(r.ignored_lead_ratio),
    },
    {
      key: 'avg_calls_per_lead',
      label: 'Avg Calls / Lead',
      fmt: (r) => r.avg_calls_per_lead.toFixed(2),
    },
  ];

function CallListCards({ data }: { data: CallRow }) {
  const cards = [
    { title: 'Total Call List Leads', primary: count(data.total_leads) },
    { title: 'Total Calls Made', primary: count(data.call_attempts) },
    { title: 'Calls Today', primary: count(data.calls_today) },
    {
      title: 'Daily Call Average',
      primary: avg(data.daily_call_avg),
      badge: '30 day rolling',
      isPlaceholder: data.daily_call_avg == null,
    },
    { title: 'In Progress', primary: count(data.in_progress) },
    { title: 'Total Dispositioned', primary: count(data.dispositioned) },

    { title: 'Quoted', primary: count(data.quoted) },
    { title: 'Declined', primary: count(data.declined) },
    { title: 'Unable to Connect', primary: count(data.unable_to_connect) },
    {
      title: 'Ignored Lead Ratio',
      primary: formatRatio(data.ignored_lead_ratio),
    },
    { title: 'Closing Ratio', primary: formatRatio(data.closing_ratio) },
    { title: 'Call to Quote Ratio', primary: avg(data.call_to_quote_ratio) },
    {
      title: 'Avg Calls Per Lead',
      primary: data.avg_calls_per_lead.toFixed(2),
    },
  ];
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="16px" mb="24px">
      {cards.map((c) => (
        <KPICard key={c.title} {...c} />
      ))}
    </SimpleGrid>
  );
}

function CallListTrendChart({ data }: { data: CallTrend[] }) {
  return (
    <Box mb="24px">
      <Text fontWeight="700" fontSize="md" color="gray.800">
        Rolling 12 Months - Call List Trend
      </Text>
      <Chart
        type="line"
        height={260}
        series={[
          {
            name: 'Daily Call Average',
            data: data.map((t) => Number(t.daily_call_avg.toFixed(1))),
          },
          {
            name: 'Call to Quote',
            data: data.map((t) => Number(t.call_to_quote_ratio.toFixed(1))),
          },
          {
            name: 'Closing %',
            data: data.map((t) => Math.round(t.closing_ratio * 100)),
          },
        ]}
        options={{
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          colors: ['#6B48FF', '#DD6B20', '#38A169'],
          stroke: { curve: 'smooth', width: 2 },
          markers: { size: 4 },
          xaxis: {
            categories: data.map((t) => formatYearMonth(t.year_month, 'short')),
          },
          legend: { position: 'top', horizontalAlign: 'right' },
        }}
      />
    </Box>
  );
}

function downloadCSV(data: CallRow[]) {
  const headers = ['Name', ...COLUMNS.map((c) => c.label)];
  const rows: string[][] = [];
  data.forEach((branch) => {
    rows.push([branch.label, ...COLUMNS.map((c) => c.fmt(branch))]);
    (branch.children ?? []).forEach((broker) => {
      rows.push([`  ${broker.label}`, ...COLUMNS.map((c) => c.fmt(broker))]);
    });
  });
  const csv = [headers, ...rows]
    .map((r) => r.map((v) => `"${v}"`).join(','))
    .join('\n');
  downloadCsvFile(csv, `call_list_${todayStr()}`);
}

interface Props {
  overall?: CallRow;
  trends: CallTrend[];
  data: CallRow[];
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
}

export const CallListTable = ({
  overall,
  trends,
  data,
  onBranchClick,
  onBrokerClick,
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('total_leads');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortRows = (rows: CallRow[]) =>
    [...rows].sort((a, b) => {
      const diff = (a[sortKey] ?? -1) - (b[sortKey] ?? -1);
      return sortDir === 'desc' ? -diff : diff;
    });

  return (
    <Box>
      {overall && <CallListCards data={overall} />}
      {trends.length > 0 && <CallListTrendChart data={trends} />}

      <Flex justify="space-between" align="center" mb="12px">
        <Text fontWeight="700" fontSize="md" color="gray.800">
          Branches & Brokers
        </Text>
        <Tooltip label={data.length === 0 ? 'No data' : 'Download CSV'}>
          <IconButton
            aria-label="Download call list"
            icon={<DownloadIcon />}
            size="sm"
            variant="outline"
            isDisabled={data.length === 0}
            onClick={() => downloadCSV(data)}
          />
        </Tooltip>
      </Flex>

      {data.length === 0 ? (
        <Box
          textAlign="center"
          color="gray.400"
          py="40px"
          borderRadius="12px"
          border="1px dashed"
          borderColor="gray.200"
        >
          No Data Available
        </Box>
      ) : (
        <Box
          overflowX="auto"
          border="1px solid"
          borderColor="gray.100"
          borderRadius="8px"
        >
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th minW="180px">Branch / Broker</Th>
                {COLUMNS.map((c) => (
                  <Th
                    key={c.key}
                    textAlign="right"
                    cursor="pointer"
                    whiteSpace="nowrap"
                    onClick={() => handleSort(c.key)}
                  >
                    {c.label}
                    {sortKey === c.key &&
                      (sortDir === 'desc' ? (
                        <TriangleDownIcon boxSize="8px" ml="4px" />
                      ) : (
                        <TriangleUpIcon boxSize="8px" ml="4px" />
                      ))}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {sortRows(data).map((branch) => {
                const isOpen = expandedId === branch.branch_id;
                return (
                  <Fragment key={branch.branch_id ?? branch.label}>
                    <Tr
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      bg={isOpen ? 'brand.50' : undefined}
                      onClick={() => {
                        setExpandedId(isOpen ? null : branch.branch_id);
                        if (!isOpen && branch.branch_id != null)
                          onBranchClick?.(branch.branch_id);
                      }}
                    >
                      <Td fontWeight="700" fontSize="sm">
                        {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}{' '}
                        {branch.label}
                      </Td>
                      {COLUMNS.map((c) => (
                        <Td key={c.key} textAlign="right" fontSize="sm">
                          {c.fmt(branch)}
                        </Td>
                      ))}
                    </Tr>
                    {isOpen &&
                      sortRows(branch.children ?? []).map((broker) => (
                        <Tr
                          key={broker.email}
                          bg="gray.50"
                          cursor={onBrokerClick ? 'pointer' : undefined}
                          onClick={() => onBrokerClick?.(broker.email)}
                        >
                          <Td pl="36px" fontSize="xs" color="gray.700">
                            {broker.label}
                          </Td>
                          {COLUMNS.map((c) => (
                            <Td key={c.key} textAlign="right" fontSize="xs">
                              {c.fmt(broker)}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
